import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkle, PaperPlaneTilt, Lightbulb, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MOODS = [
  { key: "calm", label: "calm", color: "bg-sage/25 text-sage" },
  { key: "anxious", label: "anxious", color: "bg-clay/20 text-clay-dark" },
  { key: "tender", label: "tender", color: "bg-lilac/25 text-lilac" },
  { key: "hopeful", label: "hopeful", color: "bg-sand/40 text-ink" },
  { key: "tired", label: "tired", color: "bg-ink-mute/20 text-ink-soft" },
  { key: "angry", label: "angry", color: "bg-clay/30 text-clay-dark" },
  { key: "grateful", label: "grateful", color: "bg-sage/30 text-sage" },
  { key: "lost", label: "lost", color: "bg-lilac/20 text-lilac" },
];

const BODY_SIGNALS = [
  { key: "feeling_good", label: "feeling good" },
  { key: "steady", label: "steady" },
  { key: "low_energy", label: "low energy" },
  { key: "sensitive", label: "sensitive" },
  { key: "uncomfortable", label: "uncomfortable" },
  { key: "tender", label: "tender" },
];

const todayKey = () => `clarity_body_${new Date().toISOString().slice(0, 10)}`;

export default function ComposerPage() {
  const { onEntryCreated } = useOutletContext();
  const [content, setContent] = useState("");
  const [moods, setMoods] = useState([]);
  const [bodySignal, setBodySignal] = useState(null);
  const [showBodyCheckin, setShowBodyCheckin] = useState(() => {
    return localStorage.getItem(todayKey()) === null;
  });
  const [submitting, setSubmitting] = useState(false);
  const [reflection, setReflection] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [showPrompts, setShowPrompts] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    api.get("/prompts").then((r) => setPrompts(r.data.prompts)).catch(() => {});
    // Restore draft if user was bounced to subscribe mid-entry
    const draft = localStorage.getItem("clarity_draft");
    if (draft) {
      try {
        const d = JSON.parse(draft);
        if (d.content) setContent(d.content);
        if (d.moods) setMoods(d.moods);
        if (d.body_signal) setBodySignal(d.body_signal);
      } catch {
        // corrupt draft, ignore
      }
      localStorage.removeItem("clarity_draft");
    }
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [content]);

  const toggleMood = (m) => {
    setMoods((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  };

  const submit = async () => {
    if (content.trim().length < 3) {
      toast.error("Write a little more, even one sentence.");
      return;
    }
    setSubmitting(true);
    setReflection(null);
    try {
      const { data } = await api.post("/entries", { content, moods, body_signal: bodySignal });
      setReflection(data.reflection);
      onEntryCreated();
      if (bodySignal) {
        localStorage.setItem(todayKey(), bodySignal);
        setShowBodyCheckin(false);
      }
      toast.success("Saved.");
    } catch (err) {
      if (err?.response?.status === 402) {
        // Preserve their draft, take them to subscribe
        localStorage.setItem("clarity_draft", JSON.stringify({ content, moods, body_signal: bodySignal }));
        toast.message("Your story can keep going. Choose a plan to continue.", { duration: 5000 });
        setTimeout(() => { window.location.href = "/app/subscribe"; }, 1200);
      } else {
        toast.error(err?.response?.data?.detail || "Could not save");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const dismissBodyCheckin = () => {
    localStorage.setItem(todayKey(), "skip");
    setShowBodyCheckin(false);
  };

  const pickBodySignal = (key) => {
    setBodySignal((prev) => (prev === key ? null : key));
  };

  const applyPrompt = (p) => {
    setContent((c) => (c ? c + "\n\n" + p.prompt + "\n" : p.prompt + "\n"));
    setShowPrompts(false);
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const newEntry = () => {
    setContent("");
    setMoods([]);
    setBodySignal(null);
    setReflection(null);
    textareaRef.current?.focus();
  };

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-full flex flex-col" data-testid="composer-page">
      <div className="px-5 sm:px-10 md:px-20 pt-8 sm:pt-14 pb-6 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-mute">{today}</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-ink mt-3 leading-tight">What’s true right now?</h1>
        </div>
        <button
          onClick={() => setShowPrompts((s) => !s)}
          data-testid="open-prompts"
          className="shrink-0 inline-flex items-center gap-2 rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm border border-line-strong text-ink hover:bg-canvas-warm transition-colors"
        >
          <Lightbulb size={16} weight="thin" /> <span className="hidden sm:inline">Need a prompt?</span><span className="sm:hidden">Prompt</span>
        </button>
      </div>

      <div className="flex-1 px-5 sm:px-10 md:px-20 pb-16 sm:pb-20">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Begin anywhere. Even one word counts."
          data-testid="composer-input"
          rows={6}
          className="composer-textarea min-h-[280px]"
          disabled={submitting}
        />

        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="mood-tags">
          <span className="text-xs uppercase tracking-[0.25em] text-ink-mute mr-2">Tag a mood</span>
          {MOODS.map((m) => {
            const active = moods.includes(m.key);
            return (
              <button
                key={m.key}
                onClick={() => toggleMood(m.key)}
                data-testid={`mood-${m.key}`}
                className={`text-sm px-3.5 py-1.5 rounded-full transition-all ${
                  active ? `${m.color} ring-1 ring-current` : "bg-canvas-warm text-ink-soft hover:text-ink"
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        <AnimatePresence>
          {showBodyCheckin && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              className="mt-8 overflow-hidden"
              data-testid="body-checkin"
            >
              <div className="rounded-2xl border border-line bg-canvas-warm/40 p-5 sm:p-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <p className="font-serif-display italic text-lg sm:text-xl text-ink leading-snug">
                    How is your body feeling today?
                  </p>
                  <button
                    onClick={dismissBodyCheckin}
                    data-testid="body-checkin-dismiss"
                    className="shrink-0 text-xs text-ink-mute hover:text-ink underline underline-offset-4 decoration-line-strong"
                  >
                    Not today
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {BODY_SIGNALS.map((s) => {
                    const active = bodySignal === s.key;
                    return (
                      <button
                        key={s.key}
                        onClick={() => pickBodySignal(s.key)}
                        data-testid={`body-${s.key}`}
                        className={`text-sm px-3.5 py-1.5 rounded-full transition-all ${
                          active
                            ? "bg-sage/30 text-sage ring-1 ring-sage/60"
                            : "bg-canvas text-ink-soft hover:text-ink border border-line"
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-[11px] text-ink-mute italic">
                  Optional. Only you see this, and patterns surface gently in your reflections over time.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-10 flex items-center gap-4">
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="submit-entry"
            className="group inline-flex items-center gap-2 rounded-full bg-clay text-canvas px-7 py-3 text-sm font-medium hover:bg-clay-dark transition-all active:scale-95 disabled:opacity-60"
          >
            {submitting ? (
              <>Reflecting<span className="ml-1 animate-pulse">…</span></>
            ) : (
              <>
                Save & reflect <PaperPlaneTilt size={16} weight="bold" />
              </>
            )}
          </button>
          {reflection && (
            <button
              onClick={newEntry}
              data-testid="new-entry"
              className="text-sm text-ink-soft hover:text-ink underline underline-offset-4 decoration-line-strong"
            >
              Start a new entry
            </button>
          )}
        </div>

        <AnimatePresence>
          {reflection && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              className="relative mt-12 sm:mt-14 glass-warm rounded-3xl p-6 sm:p-8 md:p-10 overflow-hidden"
              data-testid="ai-reflection-panel"
            >
              <div className="absolute left-0 top-6 bottom-6 w-[2px] tracing-beam rounded-full" />
              <div className="flex items-center gap-2 mb-6">
                <Sparkle size={16} weight="fill" className="text-clay" />
                <span className="text-xs uppercase tracking-[0.3em] text-ink-mute">The mirror notices</span>
              </div>

              {reflection.mirror && (
                <p className="font-serif-display italic text-2xl md:text-3xl text-ink leading-snug max-w-2xl mb-10">
                  {reflection.mirror}
                </p>
              )}

              <div className="grid md:grid-cols-3 gap-8">
                {reflection.emotions?.length > 0 && (
                  <Block title="Emotions">
                    <div className="flex flex-wrap gap-2">
                      {reflection.emotions.map((e) => (
                        <span key={e} className="text-sm px-3 py-1 rounded-full bg-clay/15 text-clay-dark">
                          {e}
                        </span>
                      ))}
                    </div>
                  </Block>
                )}
                {reflection.patterns?.length > 0 && (
                  <Block title="Patterns I see">
                    <ul className="space-y-2 text-ink-soft text-sm leading-relaxed">
                      {reflection.patterns.map((p) => (
                        <li key={p} className="flex gap-2">
                          <span className="text-clay">·</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </Block>
                )}
                {reflection.gentle_questions?.length > 0 && (
                  <Block title="Gentle questions">
                    <ol className="space-y-3 text-ink text-sm leading-relaxed font-serif-display text-base">
                      {reflection.gentle_questions.map((q, i) => (
                        <li key={q} className="flex gap-2 italic">
                          <span className="text-clay font-sans not-italic">{i + 1}.</span>
                          <span>{q}</span>
                        </li>
                      ))}
                    </ol>
                  </Block>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Prompts drawer */}
      <AnimatePresence>
        {showPrompts && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-ink/30 backdrop-blur-sm z-40"
            onClick={() => setShowPrompts(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="absolute bottom-0 left-0 right-0 bg-canvas border-t border-line rounded-t-3xl p-5 sm:p-8 md:p-12 max-h-[85vh] overflow-y-auto"
              data-testid="prompts-drawer"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="font-serif-display text-3xl text-ink">Guided prompts</h3>
                <button onClick={() => setShowPrompts(false)} className="text-ink-mute hover:text-ink">
                  <X size={20} />
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {prompts.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => applyPrompt(p)}
                    data-testid={`prompt-${p.id}`}
                    className="text-left p-6 rounded-2xl border border-line hover:border-clay hover:bg-clay/5 transition-colors"
                  >
                    <div className="text-xs uppercase tracking-[0.2em] text-clay mb-3">{p.category}</div>
                    <p className="font-serif-display text-xl text-ink leading-snug">{p.prompt}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.25em] text-ink-mute mb-3">{title}</p>
      {children}
    </div>
  );
}
